import type React from "react";
import {useStore} from "@xyflow/react";
import {Paper, Stack, Typography} from "@mui/material";
import {Tag} from "~/components/tag/Tag";
import {PART_SEP, PATH_SEP} from "~/constants";
import type {TaxonomyTreeNode} from "./taxonomyTreeLayout";

/** Below this zoom level the card text is too small to read. */
const SHOW_BELOW_ZOOM = 0.7;

type TaxonomyHoveredNodeCardProps = {
    node: TaxonomyTreeNode | undefined;
    /** Names of the categories above the node, L1 first */
    parents: string[];
};

/**
 * The hovered category at a readable size while the canvas is zoomed out, shown under the level
 * filters. Render inside `<ReactFlow>` (it reads the zoom).
 */
export const TaxonomyHoveredNodeCard: React.FC<TaxonomyHoveredNodeCardProps> = ({node, parents}) => {
    const zoomedOut = useStore(s => s.transform[2] < SHOW_BELOW_ZOOM);
    if (!node || !zoomedOut) return null;
    const {label, level, childCount} = node.data;
    const isRoot = level === 0;

    return (
        <Paper variant="outlined" sx={{px: 1.5, py: 1, maxWidth: 360, alignSelf: 'flex-start', boxShadow: 2}}>
            {parents.length > 0 && (
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {parents.join(PATH_SEP)}
                </Typography>
            )}
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                {!isRoot && (
                    <Tag
                        label={`L${level}`}
                        color="default"
                        variant="outlined"
                        size="small"
                        sx={{flexShrink: 0, minHeight: '14px', '&&': {borderRadius: 0.5}}}
                    />
                )}
                <Typography variant="subtitle2" fontWeight={700} noWrap minWidth={0}>{label}</Typography>
            </Stack>
            {(isRoot || childCount > 0) && (
                <Typography variant="caption" color="text.secondary" component="div">
                    {isRoot ? 'Taxonomy' : null}
                    {isRoot && childCount > 0 && PART_SEP}
                    {childCount > 0 && `${childCount} sub-categories`}
                </Typography>
            )}
        </Paper>
    );
};
